import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, X, Printer, UserPlus, Settings2, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GRADES = ["2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"];
const OPTIONAL = [
    { key: "phone", label: "Phone number" },
    { key: "email", label: "Email" },
    { key: "school", label: "High school" },
    { key: "age", label: "Age" },
    { key: "age_asof", label: "Age as of 01AUG2027" },
];

const fmtHeight = (inches) => {
    if (!inches) return "";
    const n = Number(inches);
    if (!n) return "";
    return `${Math.floor(n / 12)}'${n % 12}"`;
};
const gradeRank = (g) => { const i = GRADES.indexOf(g); return i < 0 ? -1 : i; };
const gradeFromGradYear = (gy) => {
    if (!gy) return "";
    const n = 12 - (Number(gy) - 2027);
    return n >= 2 && n <= 12 ? GRADES[n - 2] : "";
};

/** Owner: pick whether to collect RSVPs and which optional columns. */
export function RsvpConfigModal({ event, onClose, onSaved }) {
    const [enabled, setEnabled] = useState(!!event.rsvp_enabled);
    const [fields, setFields] = useState(() => {
        const f = event.rsvp_fields || {};
        return OPTIONAL.reduce((a, o) => ({ ...a, [o.key]: !!f[o.key] }), {});
    });
    const [cap, setCap] = useState(event.rsvp_cap ? String(event.rsvp_cap) : "");
    const [cutoff, setCutoff] = useState(event.rsvp_cutoff || "");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post(`/events/${event.id}/rsvp-config`, {
                enabled, fields,
                cap: cap === "" ? null : Number(cap),
                cutoff: cutoff || null,
            });
            toast.success(enabled ? "RSVPs are now being collected" : "RSVPs turned off");
            onSaved?.(data);
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
        finally { setSaving(false); }
    };
    return (
        <Overlay onClose={onClose} testid="rsvp-config-modal">
            <Header title="Accept RSVPs" onClose={onClose} />
            <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="rsvp-enable-toggle">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-[#22c55e]" />
                <span className="text-sm text-white">Collect RSVPs for this event</span>
            </label>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-2">Always collected</p>
            <div className="text-xs text-[#8a8a8a] mb-4 leading-relaxed">First &amp; last name, grade, grad year, position, height, city/state, gender.</div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-2">Also collect (optional)</p>
            <div className="grid grid-cols-1 gap-2 mb-5">
                {OPTIONAL.map((o) => (
                    <label key={o.key} className={`flex items-center gap-3 cursor-pointer ${enabled ? "" : "opacity-40 pointer-events-none"}`} data-testid={`rsvp-field-${o.key}`}>
                        <input type="checkbox" checked={!!fields[o.key]} onChange={(e) => setFields((f) => ({ ...f, [o.key]: e.target.checked }))} className="h-4 w-4 accent-[#22c55e]" />
                        <span className="text-sm text-[#e5e5e5]">{o.label}</span>
                    </label>
                ))}
            </div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#a0a0a0] mb-2">Limits (optional)</p>
            <div className={`grid grid-cols-2 gap-3 mb-5 ${enabled ? "" : "opacity-40 pointer-events-none"}`}>
                <Field label="Max RSVPs (cap)">
                    <input data-testid="rsvp-cap" value={cap} onChange={(e) => setCap(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Unlimited" className={inputCls} />
                </Field>
                <Field label="RSVP deadline">
                    <input data-testid="rsvp-cutoff" type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className={inputCls} />
                </Field>
            </div>
            <div className="text-[11px] text-[#8a8a8a] mb-5 leading-relaxed">
                Past the cap, extra sign-ups go on a <span className="text-[#e7c684]">waitlist</span> you can see. After the deadline, no new RSVPs are accepted (people already signed up can still edit or cancel).
            </div>
            <button onClick={save} disabled={saving} data-testid="rsvp-config-save" className="w-full px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors disabled:opacity-40">
                {saving ? "Saving…" : "Save"}
            </button>
        </Overlay>
    );
}

/** Athlete: submit / edit their RSVP, autofilled from their athlete profile. */
export function RsvpEntryModal({ event, onClose, onDone }) {
    const { user } = useAuth();
    const fields = event.rsvp_fields || {};
    const [form, setForm] = useState({
        first_name: "", last_name: "", grad_year: "", grade: "", position: "",
        height_inches: "", city: "", state: "", gender: "", phone: "", email: "", school: "", age: "", age_asof: "",
    });
    const [hasProfile, setHasProfile] = useState(false);
    const [existing, setExisting] = useState(false);
    const [myWaitlisted, setMyWaitlisted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            let prefill = {};
            try {
                const { data: p } = await api.get(`/players/${user.id}`);
                if (p && p.id) {
                    setHasProfile(true);
                    const parts = (p.name || "").trim().split(" ");
                    prefill = {
                        first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "",
                        grad_year: p.grad_year || "", grade: gradeFromGradYear(p.grad_year),
                        position: p.position || "", height_inches: p.height_inches || "",
                        city: p.home_city || "", state: p.home_state || "", gender: p.gender || "",
                        age: p.age || "", age_asof: p.age_as_of_2027 || "",
                    };
                    const contacts = p.contacts || [];
                    const ph = contacts.find((c) => (c.type || "").toLowerCase() === "phone");
                    const em = contacts.find((c) => (c.type || "").toLowerCase() === "email");
                    if (ph) prefill.phone = ph.value;
                    if (em) prefill.email = em.value;
                }
            } catch { /* no athlete profile */ }
            try {
                const { data: mine } = await api.get(`/events/${event.id}/rsvp/mine`);
                if (mine && mine.id) { setExisting(true); setMyWaitlisted(!!mine.waitlisted); prefill = { ...prefill, ...mine }; }
            } catch { /* none */ }
            if (active) { setForm((f) => ({ ...f, ...prefill })); setLoading(false); }
        })();
        return () => { active = false; };
    }, [event.id, user.id]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async () => {
        if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("First and last name are required");
        setSaving(true);
        try {
            const payload = { ...form };
            ["grad_year", "height_inches", "age", "age_asof"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
            const { data } = await api.post(`/events/${event.id}/rsvp`, payload);
            if (data?.waitlisted) toast.success(existing ? "RSVP updated — you're on the waitlist" : "This event is full — you've been added to the waitlist");
            else toast.success(existing ? "RSVP updated" : "You're on the list — RSVP submitted!");
            onDone?.(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not submit RSVP"); }
        finally { setSaving(false); }
    };
    const cancel = async () => {
        setSaving(true);
        try { await api.delete(`/events/${event.id}/rsvp`); toast.success("RSVP cancelled"); onDone?.(); onClose(); }
        catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
        finally { setSaving(false); }
    };

    return (
        <Overlay onClose={onClose} testid="rsvp-entry-modal">
            <Header title={`RSVP · ${event.name}`} onClose={onClose} />
            {loading ? <p className="text-sm text-[#8a8a8a]">Loading…</p> : (
                <>
                    {existing && myWaitlisted && (
                        <div className="mb-4 rounded border border-[#f59e0b]/30 bg-[#f59e0b]/[0.07] p-3 text-xs text-[#e7c684]" data-testid="rsvp-waitlist-note">
                            This event is full — you're currently on the <strong>waitlist</strong>. The organizer will reach out if a spot opens.
                        </div>
                    )}
                    {!existing && event.rsvp_cutoff && event.rsvp_cutoff < new Date().toISOString().slice(0, 10) && (
                        <div className="mb-4 rounded border border-red-500/30 bg-red-500/[0.07] p-3 text-xs text-red-300" data-testid="rsvp-deadline-note">
                            The RSVP deadline ({event.rsvp_cutoff}) has passed — new RSVPs are closed.
                        </div>
                    )}
                    {!hasProfile && (
                        <div className="mb-4 rounded border border-[#f59e0b]/30 bg-[#f59e0b]/[0.07] p-3 text-xs text-[#e7c684]" data-testid="rsvp-no-profile-prompt">
                            <UserPlus className="h-3.5 w-3.5 inline mr-1" />
                            Tip: <Link to="/profile" className="text-[#22c55e] hover:underline">create an athlete profile</Link> and your info will prefill automatically next time you RSVP.
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="First name" required><input data-testid="rsvp-first-name" value={form.first_name} onChange={set("first_name")} className={inputCls} /></Field>
                        <Field label="Last name" required><input data-testid="rsvp-last-name" value={form.last_name} onChange={set("last_name")} className={inputCls} /></Field>
                        <Field label="Grade"><select data-testid="rsvp-grade" value={form.grade} onChange={set("grade")} className={inputCls}><option value="">—</option>{GRADES.map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
                        <Field label="Grad year"><input data-testid="rsvp-grad-year" value={form.grad_year} onChange={set("grad_year")} inputMode="numeric" className={inputCls} /></Field>
                        <Field label="Position"><input data-testid="rsvp-position" value={form.position} onChange={set("position")} className={inputCls} /></Field>
                        <Field label="Height (inches)"><input data-testid="rsvp-height" value={form.height_inches} onChange={set("height_inches")} inputMode="numeric" className={inputCls} /></Field>
                        <Field label="City"><input data-testid="rsvp-city" value={form.city} onChange={set("city")} className={inputCls} /></Field>
                        <Field label="State"><input data-testid="rsvp-state" value={form.state} onChange={set("state")} className={inputCls} /></Field>
                        <Field label="Gender"><select data-testid="rsvp-gender" value={form.gender} onChange={set("gender")} className={inputCls}><option value="">—</option><option value="M">Boys</option><option value="F">Girls</option></select></Field>
                        {fields.phone && <Field label="Phone"><input data-testid="rsvp-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>}
                        {fields.email && <Field label="Email"><input data-testid="rsvp-email" value={form.email} onChange={set("email")} className={inputCls} /></Field>}
                        {fields.school && <Field label="High school"><input data-testid="rsvp-school" value={form.school} onChange={set("school")} className={inputCls} /></Field>}
                        {fields.age && <Field label="Age"><input data-testid="rsvp-age" value={form.age} onChange={set("age")} inputMode="numeric" className={inputCls} /></Field>}
                        {fields.age_asof && <Field label="Age as of 01AUG2027"><input data-testid="rsvp-age-asof" value={form.age_asof} onChange={set("age_asof")} inputMode="numeric" className={inputCls} /></Field>}
                    </div>
                    <div className="flex gap-3 mt-5">
                        <button onClick={submit} disabled={saving} data-testid="rsvp-submit" className="flex-1 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-black transition-colors disabled:opacity-40">{saving ? "Saving…" : existing ? "Update RSVP" : "Submit RSVP"}</button>
                        {existing && <button onClick={cancel} disabled={saving} data-testid="rsvp-cancel" className="px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-red-500 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-40">Cancel RSVP</button>}
                    </div>
                </>
            )}
        </Overlay>
    );
}

/** Owner: spreadsheet-style list with sort + printable (8.5x11) output. */
export function RsvpListModal({ event, onClose }) {
    const [data, setData] = useState(null);
    const [sortMode, setSortMode] = useState("grade");

    useEffect(() => {
        api.get(`/events/${event.id}/rsvps`).then(({ data }) => setData(data)).catch(() => setData({ rsvps: [], rsvp_fields: {}, count: 0 }));
    }, [event.id]);

    const fields = data?.rsvp_fields || {};
    const cap = data?.rsvp_cap || null;
    const cols = useMemo(() => {
        const c = [
            { key: "last_name", label: "Last" }, { key: "first_name", label: "First" },
            { key: "grade", label: "Grade" }, { key: "grad_year", label: "Grad Yr" },
            { key: "position", label: "Pos" }, { key: "height_inches", label: "Ht", fmt: fmtHeight },
            { key: "loc", label: "City/State", get: (r) => [r.city, r.state].filter(Boolean).join(", ") },
            { key: "gender", label: "Gender", fmt: (g) => (g === "M" ? "Boys" : g === "F" ? "Girls" : g || "") },
        ];
        if (fields.phone) c.push({ key: "phone", label: "Phone" });
        if (fields.email) c.push({ key: "email", label: "Email" });
        if (fields.school) c.push({ key: "school", label: "High School" });
        if (fields.age) c.push({ key: "age", label: "Age" });
        if (fields.age_asof) c.push({ key: "age_asof", label: "Age@8/1/27" });
        if (cap) c.push({ key: "status", label: "Status", get: (r) => (r.waitlisted ? "Waitlist" : "Confirmed") });
        return c;
    }, [fields, cap]);

    const rows = useMemo(() => {
        const list = [...(data?.rsvps || [])];
        list.sort((a, b) => {
            if (sortMode === "grade") {
                const d = gradeRank(b.grade) - gradeRank(a.grade);
                if (d !== 0) return d;
            }
            return (a.last_name || "").localeCompare(b.last_name || "");
        });
        return list;
    }, [data, sortMode]);

    const cell = (r, col) => (col.get ? col.get(r) : col.fmt ? col.fmt(r[col.key]) : (r[col.key] ?? "")) || "";

    const downloadCsv = () => {
        const headers = [...cols.map((c) => c.label), "Present", "Notes"];
        const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
        const lines = [headers.map(esc).join(",")];
        rows.forEach((r) => {
            lines.push([...cols.map((c) => esc(cell(r, c))), esc(""), esc("")].join(","));
        });
        const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safe = (data?.event_name || "event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
        a.href = url; a.download = `rsvps-${safe || "event"}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    const print = () => {
        const w = window.open("", "_blank", "width=900,height=700");
        const head = cols.map((c) => `<th>${c.label}</th>`).join("") + "<th class='present'>Present</th><th class='notes'>Notes</th>";
        const body = rows.map((r) => "<tr>" + cols.map((c) => `<td>${String(cell(r, c)).replace(/</g, "&lt;")}</td>`).join("") + "<td class='present'>&#9744;</td><td class='notes'></td></tr>").join("");
        w.document.write(`<!doctype html><html><head><title>RSVPs — ${data.event_name || ""}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          * { font-family: Arial, sans-serif; }
          h1 { font-size: 16px; margin: 0 0 2px; } .sub { font-size: 11px; color: #444; margin: 0 0 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #333; padding: 4px 6px; font-size: 10px; text-align: left; }
          th { background: #eee; text-transform: uppercase; letter-spacing: .04em; font-size: 9px; }
          td.present, th.present { width: 46px; text-align: center; font-size: 14px; }
          td.notes, th.notes { width: 34%; }
          tr { page-break-inside: avoid; }
        </style></head><body>
        <h1>${data.event_name || "Event"} — RSVPs (${rows.length})</h1>
        <div class="sub">${data.event_date || ""} · Sorted by ${sortMode === "grade" ? "grade (highest first), then last name" : "last name"}</div>
        <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </body></html>`);
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 300);
    };

    return (
        <Overlay onClose={onClose} testid="rsvp-list-modal" wide>
            <Header title={`RSVPs · ${data?.event_name || event.name}`} onClose={onClose} />
            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="text-sm text-white font-semibold" data-testid="rsvp-count">{data?.count ?? 0} total</span>
                {cap && (
                    <span className="text-[11px] text-[#a0a0a0]" data-testid="rsvp-cap-summary">
                        {Math.min(data?.count ?? 0, cap)} confirmed / {Math.max((data?.count ?? 0) - cap, 0)} waitlist · cap {cap}
                    </span>
                )}
                {data?.rsvp_cutoff && (
                    <span className="text-[11px] text-[#a0a0a0]" data-testid="rsvp-cutoff-summary">deadline {data.rsvp_cutoff}</span>
                )}
                <div className="inline-flex rounded overflow-hidden border border-[#2a2a2a] ml-auto">
                    <button onClick={() => setSortMode("grade")} data-testid="rsvp-sort-grade" className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.12em] ${sortMode === "grade" ? "bg-[#22c55e] text-black" : "bg-[#0e0e0e] text-[#a0a0a0]"}`}>By grade</button>
                    <button onClick={() => setSortMode("last")} data-testid="rsvp-sort-last" className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.12em] ${sortMode === "last" ? "bg-[#22c55e] text-black" : "bg-[#0e0e0e] text-[#a0a0a0]"}`}>By last name</button>
                </div>
                <button onClick={downloadCsv} disabled={!rows.length} data-testid="rsvp-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-[#2a2a2a] text-white hover:border-[#22c55e] hover:text-[#22c55e] transition-colors disabled:opacity-40"><Download className="h-3.5 w-3.5" /> CSV</button>
                <button onClick={print} disabled={!rows.length} data-testid="rsvp-print" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-[#2a2a2a] text-white hover:border-[#22c55e] hover:text-[#22c55e] transition-colors disabled:opacity-40"><Printer className="h-3.5 w-3.5" /> Print</button>
            </div>
            <div className="overflow-auto max-h-[60vh] border border-[#2a2a2a]">
                <table className="w-full text-xs" data-testid="rsvp-table">
                    <thead className="bg-[#0e0e0e] sticky top-0">
                        <tr>
                            {cols.map((c) => <th key={c.key} className="text-left px-2.5 py-2 text-[10px] uppercase tracking-[0.1em] text-[#a0a0a0] border-b border-[#2a2a2a] whitespace-nowrap">{c.label}</th>)}
                            <th className="px-2.5 py-2 text-[10px] uppercase text-[#a0a0a0] border-b border-[#2a2a2a]">Present</th>
                            <th className="px-2.5 py-2 text-[10px] uppercase text-[#a0a0a0] border-b border-[#2a2a2a] w-1/3">Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className={r.waitlisted ? "bg-[#f59e0b]/[0.06]" : "odd:bg-[#111] even:bg-[#161616]"} data-testid="rsvp-row">
                                {cols.map((c) => <td key={c.key} className="px-2.5 py-2 text-[#e5e5e5] border-b border-[#222] whitespace-nowrap">{cell(r, c)}</td>)}
                                <td className="px-2.5 py-2 text-center border-b border-[#222]"><span className="inline-block h-3.5 w-3.5 border border-[#666]" /></td>
                                <td className="border-b border-[#222]" />
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={cols.length + 2} className="px-2.5 py-6 text-center text-[#6a6a6a]">No RSVPs yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </Overlay>
    );
}

/** Buttons shown on the event page / account panel. */
export function RsvpButtons({ event, isOwner, onConfigChange, compact }) {
    const { user } = useAuth();
    const [modal, setModal] = useState(null);   // "config" | "entry" | "list"
    const btn = compact
        ? "inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded text-[10px] uppercase tracking-[0.14em]"
        : "flex-1 h-11 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider font-semibold transition-colors";
    return (
        <>
            {isOwner ? (
                <div className={compact ? "flex gap-2 flex-wrap" : "flex items-stretch gap-3"}>
                    <button onClick={() => setModal("config")} data-testid="rsvp-accept-btn" className={`${btn} border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10`}>
                        <Settings2 className="h-3.5 w-3.5" /> {event.rsvp_enabled ? "RSVP settings" : "Accept RSVPs"}
                    </button>
                    <button onClick={() => setModal("list")} data-testid="rsvp-view-btn" className={`${btn} border border-[#2a2a2a] text-white hover:border-[#22c55e] hover:text-[#22c55e]`}>
                        <ClipboardList className="h-3.5 w-3.5" /> View RSVPs
                    </button>
                </div>
            ) : (event.rsvp_enabled && user ? (
                <button onClick={() => setModal("entry")} data-testid="rsvp-btn" className={`${btn} w-full bg-[#22c55e] text-black hover:bg-[#16a34a]`}>
                    <ClipboardList className="h-3.5 w-3.5" /> RSVP to this event
                </button>
            ) : null)}

            {modal === "config" && <RsvpConfigModal event={event} onClose={() => setModal(null)} onSaved={onConfigChange} />}
            {modal === "entry" && <RsvpEntryModal event={event} onClose={() => setModal(null)} />}
            {modal === "list" && <RsvpListModal event={event} onClose={() => setModal(null)} />}
        </>
    );
}

// --- small shared building blocks ---
const inputCls = "w-full bg-[#0e0e0e] border border-[#2a2a2a] px-2.5 py-2 text-sm text-white outline-none focus:border-[#22c55e]";
function Overlay({ children, onClose, testid, wide }) {
    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
            <div className={`w-full ${wide ? "max-w-4xl" : "max-w-lg"} bg-[#141414] border border-[#2a2a2a] p-5 max-h-[90vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>{children}</div>
        </div>
    );
}
function Header({ title, onClose }) {
    return (
        <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] uppercase tracking-[0.14em] text-white font-semibold truncate pr-3">{title}</span>
            <button onClick={onClose} className="text-[#a0a0a0] hover:text-white shrink-0" data-testid="rsvp-modal-close"><X className="h-4 w-4" /></button>
        </div>
    );
}
function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.14em] text-[#a0a0a0] mb-1">{label}{required && <span className="text-red-400"> *</span>}</span>
            {children}
        </label>
    );
}
